import { Fragment, useEffect, useMemo } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useMap } from 'react-map-gl/mapbox';
import {
  distinctFeatureStateSources,
  featureStateCase,
  featureStateLayerIds,
  featureStateSourceKey,
  featureStateZoomInterp,
  HIGHLIGHT_FEATURE_STATE_KEY,
} from './featureStateHighlight.helpers';
import {
  CASING_COLOR,
  GLOW_COLOR,
  LINE_CASING_WIDTH,
  LINE_CORE_WIDTH,
  LINE_GLOW_BLUR,
  LINE_GLOW_WIDTH,
  POLYGON_FILL_OPACITY,
} from './HighlightLayer';
import type { FeatureRef, SidebarSection } from './MetadataSidebar/buildSidebarSections';

interface Props {
  // Every section the sidebar currently RENDERS — MetadataSidebar's `visible`, which narrows to
  // the selected card in single-select. The layer stacks are mounted from the distinct sources in
  // here, not from `target`. See distinctFeatureStateSources for why.
  sections: SidebarSection[];
  target: { featureRef: FeatureRef; color?: string } | null;
}

// Highlights the WHOLE identified feature (EXP-5). The paint layers are bound to the feature's own
// vector source and every paint value is driven by feature state, so all of a multi-tile feature's
// loaded fragments (a county) paint as one shape instead of the single tile-clipped fragment a
// GeoJSON highlight source can hold.
//
// One stack is mounted per distinct source in `sections` — the sections the sidebar is rendering,
// so a source no card can reach never pays for a stack — for the life of the sidebar. Layer ids
// are derived from the source key so the stacks coexist, and each stack's `source` / `source-layer`
// are fixed at mount — which matters because react-map-gl only diffs beforeId/layout/paint/filter
// on an existing layer and never re-applies source, so a layer whose id outlived a source change
// would keep painting the PREVIOUS source. Hover changes only `target`, and therefore only feature
// state plus the core color: a draw-time property and a setPaintProperty, neither of which touches
// the style's layer list.
//
// LINE_GLOW_WIDTH is passed to every band as the OUTER width: it sets the single scale factor the
// polygon stroke is shrunk by, so the three bands stay distinct instead of collapsing onto one
// another at the cap (see polygonSafeWidth). The glow passes its own widths because it IS the
// outer band.
//
// Feature state drives PAINT, never `filter`: mapbox-gl refuses a feature-state filter
// ('["feature-state"] expression is not supported in a filter'), because filters run at tile-parse
// time and feature state is applied at draw time. Non-highlighted features therefore stay in these
// layers and are painted at zero width / zero opacity.
export default function FeatureStateHighlight({ sections, target }: Props) {
  const { current: map } = useMap();

  useEffect(() => {
    if (!map || !target) return;
    const { sourceId, sourceLayer, featureId } = target.featureRef;
    const ref = { source: sourceId, sourceLayer, id: featureId };

    // A basemap switch calls setStyle on THIS map instance while the sidebar stays mounted
    // (BasemapPicker, Historical Imagery, the download previews), which is two problems at once:
    //
    // 1. The new style rebuilds every source cache, so the flag is gone. The paint layers come back
    //    on their own — react-map-gl's Layer re-runs addLayer on 'styledata' — but this effect's
    //    deps don't change on a style swap, so without a re-apply the sidebar keeps showing a
    //    selection the map no longer highlights.
    // 2. set/removeFeatureState THROW ('Style is not done loading', mapbox-gl Style._checkLoaded)
    //    while the new style is mid-load, and a throw here escapes into React and unmounts the map.
    //
    // Do NOT gate this on map.isStyleLoaded(): that is Style.loaded(), which is far stricter than
    // the _loaded flag _checkLoaded tests — it also returns false while ANY source cache still has
    // tiles in flight, which on this map is most of the time. Gating on it suppressed virtually
    // every write and broke highlighting outright. try/catch matches the actual throw condition;
    // `applied` keeps the retry from rewriting state that already landed.
    let applied = false;
    const apply = () => {
      // The source is re-added by react-map-gl on a later 'styledata' than 'style.load', so an
      // early attempt just finds nothing; a missing source is an ErrorEvent, not a throw, and
      // skipping it keeps the console clean until the retry lands. A source can also disappear
      // WITHOUT a style swap — a TOC toggle unmounts it — which drops the flag just the same, so
      // clear `applied` on the way past and let a later retry re-flag it.
      if (!map.getSource(sourceId)) {
        applied = false;
        return;
      }
      if (applied) return;
      try {
        map.setFeatureState(ref, { [HIGHLIGHT_FEATURE_STATE_KEY]: true });
        applied = true;
      } catch {
        // Style mid-load — a later 'styledata' retries.
      }
    };
    const reapply = () => {
      applied = false;
      apply();
    };

    apply();
    map.on('style.load', reapply);
    map.on('styledata', apply);

    return () => {
      map.off('style.load', reapply);
      map.off('styledata', apply);
      // Feature state survives tile reloads, so it MUST be removed — a leaked `true` keeps a
      // stale feature glowing for the rest of the session. Nothing to remove if it never landed.
      if (!applied) return;
      try {
        map.removeFeatureState(ref, HIGHLIGHT_FEATURE_STATE_KEY);
      } catch {
        // Style mid-load, which already dropped the state.
      }
    };
  }, [map, target]);

  const sources = useMemo(() => distinctFeatureStateSources(sections), [sections]);

  if (sources.length === 0) return null;

  // Only the targeted source's stack can have a highlighted feature, so it is the only one whose
  // core color is ever visible; the rest keep the default and never repaint on hover.
  const activeKey = target ? featureStateSourceKey(target.featureRef) : null;

  return (
    <>
      {sources.map(({ key, sourceId, sourceLayer }) => {
        const ids = featureStateLayerIds(key);
        const core = (key === activeKey ? target?.color : undefined) ?? GLOW_COLOR;

        return (
          <Fragment key={key}>
            <MapGL.Layer
              id={ids.fill}
              type="fill"
              source={sourceId}
              source-layer={sourceLayer}
              paint={{ 'fill-color': core, 'fill-opacity': featureStateCase(POLYGON_FILL_OPACITY, 0) }}
            />
            <MapGL.Layer
              id={ids.glow}
              type="line"
              source={sourceId}
              source-layer={sourceLayer}
              layout={{ 'line-cap': 'round', 'line-join': 'round' }}
              paint={{
                'line-color': GLOW_COLOR,
                'line-width': featureStateZoomInterp(LINE_GLOW_WIDTH, LINE_GLOW_WIDTH),
                'line-blur': LINE_GLOW_BLUR,
              }}
            />
            <MapGL.Layer
              id={ids.casing}
              type="line"
              source={sourceId}
              source-layer={sourceLayer}
              layout={{ 'line-cap': 'round', 'line-join': 'round' }}
              paint={{
                'line-color': CASING_COLOR,
                'line-width': featureStateZoomInterp(LINE_CASING_WIDTH, LINE_GLOW_WIDTH),
              }}
            />
            <MapGL.Layer
              id={ids.core}
              type="line"
              source={sourceId}
              source-layer={sourceLayer}
              layout={{ 'line-cap': 'round', 'line-join': 'round' }}
              paint={{ 'line-color': core, 'line-width': featureStateZoomInterp(LINE_CORE_WIDTH, LINE_GLOW_WIDTH) }}
            />
          </Fragment>
        );
      })}
    </>
  );
}
